import { Link, Outlet, useNavigate } from "react-router-dom";
import { Sparkle, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Minimal layout for unauthenticated public pages (legal/info/pricing).
 * Renders only the IdeasMine wordmark + a "Back" link, so visitors can
 * read Terms / Privacy / Contact / Pricing before signing up without
 * being trapped inside the authenticated app shell. Logged-in users who
 * land here (e.g. via the hamburger / footer "Pricing" link) get the
 * same view but the logo routes back to the app home, not /auth.
 */
export default function PublicLayout() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const homeHref = user ? "/" : "/auth";
    return (
        <div className="min-h-screen bg-background" data-testid="public-layout">
            <header className="sticky top-0 z-30 bg-background/80 backdrop-blur border-b border-border">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between">
                    <Link to={homeHref} className="flex items-center gap-2" data-testid="public-home-link">
                        <div className="h-8 w-8 rounded-lg bg-[#6C63FF] grid place-items-center text-white">
                            <Sparkle size={18} weight="fill" />
                        </div>
                        <div className="font-display font-black text-lg tracking-tight">
                            <span className="text-[#6C63FF]">Ideas</span><span className="text-[#FF8A00]">Mine</span>
                        </div>
                    </Link>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="text-xs sm:text-sm font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1"
                        data-testid="public-back-btn"
                    >
                        <ArrowLeft size={14} weight="bold" /> Back
                    </button>
                </div>
            </header>
            <Outlet />
            <footer className="border-t border-border mt-12" data-testid="public-footer">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-6 text-xs text-muted-foreground space-y-3">
                    <div className="flex flex-wrap gap-4 justify-between">
                        <span>© {new Date().getFullYear()} Inovera Limited</span>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/how-it-works" className="hover:text-foreground">How It Works</Link>
                            <Link to="/subscription" className="hover:text-foreground">Pricing</Link>
                            <Link to="/terms" className="hover:text-foreground">Terms &amp; Conditions</Link>
                            <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
                            <Link to="/refund-policy" className="hover:text-foreground">Refund Policy</Link>
                            <Link to="/contact" className="hover:text-foreground">Contact Support</Link>
                        </div>
                    </div>
                    <div className="text-[11px] leading-relaxed opacity-80" data-testid="footer-company-info">
                        Ideas Mine is operated by{" "}
                        <strong>Inovera Limited</strong>, a private limited
                        company registered in Scotland, United Kingdom.{" "}
                        Company No. <span className="font-mono">SC874398</span>.
                        Support:{" "}
                        <a
                            href="mailto:support@ideasmine.co.uk"
                            className="hover:text-foreground underline-offset-2 hover:underline"
                        >
                            support@ideasmine.co.uk
                        </a>.
                    </div>
                </div>
            </footer>
        </div>
    );
}
